export type StartFieldKey =
  | "title"
  | "description"
  | "stepsToReproduce"
  | "expectedBehavior"
  | "actualBehavior"
  | "severity"
  | "userStory"
  | "acceptanceCriteria"
  | "scope"
  | "constraints"
  | "motivation"
  | "targetFiles"
  | "approach"
  | "riskLevel";

export type StartFieldWorkflowType = "bug" | "feature" | "refactor";

export interface StartFieldOption {
  key: StartFieldKey;
  label: string;
  description?: string;
}

const COMMON_FIELDS: StartFieldOption[] = [
  { key: "title", label: "Title" },
  { key: "description", label: "Description" },
];

const BUG_FIELDS: StartFieldOption[] = [
  { key: "stepsToReproduce", label: "Steps To Reproduce" },
  { key: "expectedBehavior", label: "Expected Behavior" },
  { key: "actualBehavior", label: "Actual Behavior" },
  { key: "severity", label: "Severity" },
];

const FEATURE_FIELDS: StartFieldOption[] = [
  { key: "userStory", label: "User Story" },
  { key: "acceptanceCriteria", label: "Acceptance Criteria" },
  { key: "scope", label: "Scope" },
  { key: "constraints", label: "Constraints" },
];

const REFACTOR_FIELDS: StartFieldOption[] = [
  { key: "motivation", label: "Motivation" },
  { key: "targetFiles", label: "Target Files" },
  { key: "approach", label: "Approach" },
  { key: "riskLevel", label: "Risk Level" },
];

const DEFAULT_REQUIRED_FIELDS: Record<StartFieldWorkflowType, StartFieldKey[]> = {
  bug: ["title", "description", "stepsToReproduce", "expectedBehavior", "actualBehavior"],
  feature: ["title", "description", "userStory", "acceptanceCriteria"],
  refactor: ["title", "description", "motivation", "targetFiles", "approach"],
};

const FIELD_LABELS: Record<StartFieldKey, string> = {
  title: "Title",
  description: "Description",
  stepsToReproduce: "Steps To Reproduce",
  expectedBehavior: "Expected Behavior",
  actualBehavior: "Actual Behavior",
  severity: "Severity",
  userStory: "User Story",
  acceptanceCriteria: "Acceptance Criteria",
  scope: "Scope",
  constraints: "Constraints",
  motivation: "Motivation",
  targetFiles: "Target Files",
  approach: "Approach",
  riskLevel: "Risk Level",
};

function uniqueKeys(keys: StartFieldKey[]): StartFieldKey[] {
  return Array.from(new Set(keys));
}

export function normalizeWorkflowIssueType(issueType?: string): StartFieldWorkflowType {
  if (issueType === "bug" || issueType === "hotfix") return "bug";
  if (issueType === "wp" || issueType === "refactor") return "refactor";
  return "feature";
}

export function getStartFieldOptions(issueType?: string): StartFieldOption[] {
  const normalized = normalizeWorkflowIssueType(issueType);
  if (normalized === "bug") return [...COMMON_FIELDS, ...BUG_FIELDS];
  if (normalized === "refactor") return [...COMMON_FIELDS, ...REFACTOR_FIELDS];
  return [...COMMON_FIELDS, ...FEATURE_FIELDS];
}

export function getDefaultStartRequiredFields(issueType?: string): StartFieldKey[] {
  return [...DEFAULT_REQUIRED_FIELDS[normalizeWorkflowIssueType(issueType)]];
}

export function getRequiredStartFields(configValue: unknown, issueType?: string): StartFieldKey[] {
  if (Array.isArray(configValue)) {
    const options = getStartFieldOptions(issueType);
    const allowed = new Set(options.map((option) => option.key));
    const filtered = configValue.filter(
      (value): value is StartFieldKey => typeof value === "string" && allowed.has(value as StartFieldKey),
    );
    return uniqueKeys(filtered);
  }
  return getDefaultStartRequiredFields(issueType);
}

export function isStartFieldRequired(requiredFields: readonly StartFieldKey[], field: StartFieldKey): boolean {
  return requiredFields.includes(field);
}

export function getStartFieldLabel(field: string): string {
  if (field in FIELD_LABELS) {
    return FIELD_LABELS[field as StartFieldKey];
  }
  return field
    .replace(/([A-Z])/g, " $1")
    .replace(/_/g, " ")
    .trim()
    .replace(/^./, (letter) => letter.toUpperCase());
}
